import { consola } from 'consola'
import { ref, computed } from 'vue';
import { useUserProfileStore , apiAddress as apiAddressInstance, toSelectedDeliveryAddress } from '@entities/user';
import type { Address, CreateAddressPayload, UpdateAddressPayload } from '@entities/user';

import { useAuthState } from '~/entities/auth';

/**
 * Composable для работы с адресами доставки пользователя
 * Использует новую архитектуру API /users/addresses
 * 
 * ✅ Все запросы автоматически повторяются при 401 ошибке (истечение access token)
 */
export function useAddresses() {
  const addresses = ref<Address[]>([]);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const profileStore = useUserProfileStore();
  const { isLoggedIn } = useAuthState();

  /**
   * Получает список всех адресов пользователя
   * ✅ С автоматическим повтором при 401
   * ✅ Автоматически устанавливает адрес по умолчанию в selectedDeliveryAddress
   * ✅ Проверяет авторизацию перед запросом
   */
  const fetchAddresses = async (): Promise<void> => {
    // Проверяем авторизацию перед запросом
    if (!isLoggedIn.value) {
      consola.debug('⚠️ Пользователь не авторизован, пропускаем загрузку адресов');
      addresses.value = [];
      return;
    }

    isLoading.value = true;
    error.value = null;

    try {
      addresses.value = await apiAddressInstance.getAddresses();
      
      // Автоматически устанавливаем адрес по умолчанию в selectedDeliveryAddress.
      // Координаты обязательно вместе с адресом: по ним бэкенд подбирает филиал
      // при оформлении, без них заказ на доставку не пройдёт.
      const defaultAddress = addresses.value.find(addr => addr.isDefault);
      if (defaultAddress) {
        await profileStore.setSelectedDeliveryAddress(toSelectedDeliveryAddress(defaultAddress));
      }
    } catch (error_: any) {
      error.value = error_?.data?.message || error_?.message || 'Ошибка загрузки адресов';
      consola.error('❌ Ошибка загрузки адресов:', error_);
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Создает новый адрес доставки
   * ✅ С автоматическим повтором при 401
   * ✅ Проверяет авторизацию перед запросом
   */
  const createAddress = async (payload: CreateAddressPayload): Promise<boolean> => {
    if (!isLoggedIn.value) {
      consola.warn('⚠️ Попытка создать адрес без авторизации');
      return false;
    }

    isLoading.value = true;
    error.value = null;

    try {
      await apiAddressInstance.createAddress(payload);
      await fetchAddresses(); // Перезагружаем список адресов
      isLoading.value = false;
      return true;
    } catch (error_: any) {
      error.value = error_?.data?.message || error_?.message || 'Ошибка создания адреса';
      consola.error('❌ Ошибка создания адреса:', error_);
      isLoading.value = false;
      return false;
    }
  };

  /**
   * Обновляет существующий адрес доставки
   * ✅ С автоматическим повтором при 401
   * ✅ Проверяет авторизацию перед запросом
   */
  const updateAddress = async (addressId: string, payload: UpdateAddressPayload): Promise<boolean> => {
    if (!isLoggedIn.value) {
      consola.warn('⚠️ Попытка обновить адрес без авторизации');
      return false;
    }

    isLoading.value = true;
    error.value = null;

    try {
      await apiAddressInstance.updateAddress(addressId, payload);
      await fetchAddresses(); // Перезагружаем список адресов
      isLoading.value = false;
      return true;
    } catch (error_: any) {
      error.value = error_?.data?.message || error_?.message || 'Ошибка обновления адреса';
      consola.error('❌ Ошибка обновления адреса:', error_);
      isLoading.value = false;
      return false;
    }
  };

  /**
   * Удаляет адрес доставки
   * ✅ С автоматическим повтором при 401
   * ✅ Проверяет авторизацию перед запросом
   */
  const deleteAddress = async (addressId: string): Promise<boolean> => {
    if (!isLoggedIn.value) {
      consola.warn('⚠️ Попытка удалить адрес без авторизации');
      return false;
    }

    isLoading.value = true;
    error.value = null;

    try {
      await apiAddressInstance.deleteAddress(addressId);
      await fetchAddresses(); // Перезагружаем список адресов
      isLoading.value = false;
      return true;
    } catch (error_: any) {
      error.value = error_?.data?.message || error_?.message || 'Ошибка удаления адреса';
      consola.error('❌ Ошибка удаления адреса:', error_);
      isLoading.value = false;
      return false;
    }
  };

  /**
   * Устанавливает адрес как адрес по умолчанию
   * ✅ С автоматическим повтором при 401
   * ✅ Автоматически обновляет selectedDeliveryAddress после установки
   * ✅ Проверяет авторизацию перед запросом
   */
  const setDefaultAddress = async (addressId: string): Promise<boolean> => {
    if (!isLoggedIn.value) {
      consola.warn('⚠️ Попытка установить адрес по умолчанию без авторизации');
      return false;
    }

    isLoading.value = true;
    error.value = null;

    try {
      await apiAddressInstance.setDefaultAddress(addressId);
      await fetchAddresses(); // Перезагружаем список адресов (автоматически установит новый дефолтный адрес)
      isLoading.value = false;
      return true;
    } catch (error_: any) {
      error.value = error_?.data?.message || error_?.message || 'Ошибка установки адреса по умолчанию';
      consola.error('❌ Ошибка установки адреса по умолчанию:', error_);
      isLoading.value = false;
      return false;
    }
  };

  /**
   * Получает адрес по умолчанию
   */
  const defaultAddress = computed(() => {
    return addresses.value.find(addr => addr.isDefault) || null;
  });

  /**
   * Получает активные адреса
   */
  const activeAddresses = computed(() => {
    return addresses.value.filter(addr => addr.isActive);
  });

  /**
   * Проверяет, есть ли адреса у пользователя
   */
  const hasAddresses = computed(() => {
    return addresses.value.length > 0;
  });

  return {
    // State
    addresses,
    isLoading,
    error,

    // Actions
    fetchAddresses,
    createAddress,
    updateAddress,
    deleteAddress,
    setDefaultAddress,

    // Computed
    defaultAddress,
    activeAddresses,
    hasAddresses,
  };
}

